import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { registrationAction } from '../../_actions';
import { AgreementDialog } from './AgreementDialog';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { profileConstant } from '../../_constants';

// Dynamic branding from profileConstant (differs between SVM master and ISM instoremed-master branches)
const clinicName = profileConstant.CLINIC_NAME || 'South Vancouver Medical Clinic';
const websiteUrl = profileConstant.DOMAIN_NAME || 'AskMedication.com';
const serviceEmail = profileConstant.SERVICE_EMAIL || 'info@SouthVanMed.com';

// Dynamic theme colors
const themeColor = profileConstant.THEME_PRIMARY || '#3498db';
const contactBgColor = profileConstant.THEME_CONTACT_BG || '#e8f4fc';

// Styling for the content (dynamic theme)
const styles = {
    sectionTitle: {
        color: '#2c3e50',
        fontSize: '1.1rem',
        fontWeight: '600',
        marginTop: '1.5rem',
        marginBottom: '0.75rem',
        paddingBottom: '0.5rem',
        borderBottom: `2px solid ${themeColor}`
    },
    paragraph: {
        marginBottom: '1rem',
        color: '#444',
        lineHeight: '1.6',
        textAlign: 'justify'
    },
    emergencyBox: {
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1.5rem'
    },
    emergencyTitle: {
        color: '#856404',
        fontWeight: '700',
        marginBottom: '0.5rem'
    },
    list: {
        paddingLeft: '1.5rem',
        marginBottom: '1rem'
    },
    listItem: {
        marginBottom: '0.5rem',
        color: '#444'
    },
    versionInfo: {
        color: '#7f8c8d',
        fontSize: '0.9rem',
        marginBottom: '1rem'
    },
    link: {
        color: themeColor,
        textDecoration: 'underline',
        cursor: 'pointer'
    },
    contactBlock: {
        backgroundColor: contactBgColor,
        borderRadius: '8px',
        padding: '1rem',
        marginTop: '1rem'
    }
};

class TermsOfUseDialog extends React.Component {
    state = { showFeesModal: false };

    constructor(props) {
        super(props);
        this.website = websiteUrl;
        this.company = clinicName;
    }

    handleFeesModalOpen = () => {
        this.setState({ showFeesModal: true });
    };

    handleFeesModalClose = () => {
        this.setState({ showFeesModal: false });
    };

    handlePrivacyPolicy = () => {
        const { dispatch } = this.props;
        dispatch(registrationAction.closeTermsOfUse());
        dispatch(registrationAction.closeFeeSchedule());
        dispatch(registrationAction.openPrivacyPolicy());
    };

    handleFeeSchedule = () => {
        const { dispatch } = this.props;
        dispatch(registrationAction.closePrivacyPolicy());
        dispatch(registrationAction.closeTermsOfUse());
        dispatch(registrationAction.openFeeSchedule());
    };

    handleClose = (checked) => {
        const { dispatch } = this.props;
        dispatch(registrationAction.closeTermsOfUse(checked));
    };

    termsOfUserContent() {
        return (
            <>
                <div className="title">
                    <div>{websiteUrl}</div>
                    <div>TERMS OF USE</div>
                </div>

                <p style={{ textAlign: 'center', marginBottom: '1rem' }}>
                    Please read this document completely. Continuing onto {websiteUrl} means that you agree with these Terms of Use.
                </p>

                <p style={styles.versionInfo}>Last updated: January 20, 2026 – Version 3.0</p>

                <p style={styles.paragraph}>
                    {clinicName} ("{clinicName}", "we", "us", or "our") owns and operates the website {websiteUrl}, including any subdomains (the "Website"). These Terms of Use (the "Terms"), together with our <a style={styles.link} href="#" onClick={e => { e.preventDefault(); this.handlePrivacyPolicy(); }}>Privacy Policy</a> and <a style={styles.link} href="#" onClick={e => { e.preventDefault(); this.handleFeeSchedule(); }}>Fee Schedule</a>, govern your access to and use of the Website and any associated patient portal (the "Portal"). By accessing the Website or Portal you acknowledge that you have read, understood, and agree to be bound by these Terms, the Privacy Policy, and the Fee Schedule. If you do not agree, you must stop using the Website and Portal immediately.
                </p>

                {/* Emergency Warning Box */}
                <div style={styles.emergencyBox}>
                    <div style={styles.emergencyTitle}>IMPORTANT – MEDICAL EMERGENCIES</div>
                    <p style={{ margin: 0, color: '#856404' }}>
                        The Website and Portal are not designed for medical emergencies. Do not use the Website or Portal to seek urgent medical care. If you are experiencing an urgent or life-threatening medical issue, call 9-1-1 immediately or attend the nearest emergency department or urgent care centre.
                    </p>
                </div>

                {/* Acceptance Section */}
                <h3 style={styles.sectionTitle}>ACCEPTANCE AND CHANGES TO TERMS</h3>
                <p style={styles.paragraph}>
                    These Terms form a legally binding electronic agreement between you and {clinicName}. We may update these Terms from time to time to reflect changes in our services, technology, or applicable laws in British Columbia and Canada. Updated Terms become effective when posted on the Website. We will make reasonable efforts to notify users of significant changes by email or website notice. Your continued use of the Website or Portal after changes are posted constitutes acceptance of the revised Terms.
                </p>

                {/* Permitted Use Section */}
                <h3 style={styles.sectionTitle}>PERMITTED USE</h3>
                <p style={styles.paragraph}>
                    You may use the Website and Portal solely for lawful, personal health-care related purposes. You agree not to:
                </p>
                <ul style={styles.list}>
                    <li style={styles.listItem}>use the Website or Portal for any unlawful, fraudulent, abusive, or commercial purpose;</li>
                    <li style={styles.listItem}>upload or transmit viruses, malware, or any harmful code;</li>
                    <li style={styles.listItem}>send unsolicited advertising, spam, or mass communications;</li>
                    <li style={styles.listItem}>collect information about other users without consent;</li>
                    <li style={styles.listItem}>interfere with the operation or security of the Website or Portal;</li>
                    <li style={styles.listItem}>attempt unauthorized access to any system or data;</li>
                    <li style={styles.listItem}>use automated tools to scrape or copy content;</li>
                    <li style={styles.listItem}>copy, modify, reverse engineer, or resell any part of the Website or Portal.</li>
                </ul>

                {/* The Patient Portal Section */}
                <h3 style={styles.sectionTitle}>THE PATIENT PORTAL</h3>
                <p style={styles.paragraph}>
                    The Portal is a secure online platform that allows patients to access certain services provided by physicians licensed in British Columbia (the "Services"), which may include appointment booking, messaging, forms, and payment processing.
                </p>
                <p style={styles.paragraph}>
                    To use the Portal you must create a User Account and provide accurate identification and personal information. You agree to:
                </p>
                <ul style={styles.list}>
                    <li style={styles.listItem}>keep your login credentials confidential;</li>
                    <li style={styles.listItem}>notify us immediately of any suspected unauthorized access;</li>
                    <li style={styles.listItem}>use the Portal only for your own personal health matters;</li>
                    <li style={styles.listItem}>ensure all information you submit is true, current, and complete.</li>
                </ul>
                <p style={styles.paragraph}>
                    We may suspend or terminate any User Account at our discretion for breach of these Terms or misuse of the Portal.
                </p>

                {/* Scope of Services Section */}
                <h3 style={styles.sectionTitle}>SCOPE OF SERVICES</h3>
                <p style={styles.paragraph}>
                    The Portal supports communication and administrative steps related to care. It does not replace an in-person medical assessment when clinically required and does not itself provide medical diagnosis. Clinical decisions remain at the discretion of the treating clinician in accordance with standards of the College of Physicians and Surgeons of British Columbia and applicable laws.
                </p>

                {/* Virtual Care Section */}
                <h3 style={styles.sectionTitle}>VIRTUAL CARE SERVICES AND CONSENT</h3>
                <p style={styles.paragraph}>
                    Where services are provided by telephone, video, secure messaging, or other electronic means ("Virtual Care"), you acknowledge and agree that:
                </p>
                <ul style={styles.list}>
                    <li style={styles.listItem}>Virtual Care may not be appropriate for all medical conditions and does not replace in-person assessment when clinically required;</li>
                    <li style={styles.listItem}>technology failures, poor connectivity, or privacy risks may occur despite reasonable safeguards;</li>
                    <li style={styles.listItem}>clinicians may determine that an in-person visit is necessary for safe care;</li>
                    <li style={styles.listItem}>you are responsible for attending Virtual Care sessions from a private and safe location and for protecting your own device security.</li>
                </ul>
                <p style={styles.paragraph}>
                    By using the Portal or participating in Virtual Care you consent to receive assessment, advice, and treatment through electronic means in accordance with standards of the College of Physicians and Surgeons of British Columbia.
                </p>

                {/* Minors Section */}
                <h3 style={styles.sectionTitle}>MINORS AND PARENT/GUARDIAN USE</h3>
                <p style={styles.paragraph}>
                    If the patient is under 19 years of age, a parent or legal guardian must create or authorize the User Account unless the minor is legally capable of consenting to their own health care under British Columbia law. The parent or guardian represents and warrants that:
                </p>
                <ul style={styles.list}>
                    <li style={styles.listItem}>they have legal authority to make decisions for the minor;</li>
                    <li style={styles.listItem}>all information provided on behalf of the minor is accurate;</li>
                    <li style={styles.listItem}>access to the Portal will be supervised in a manner that protects the minor's privacy.</li>
                </ul>
                <p style={styles.paragraph}>
                    Where a minor is capable of consenting to their own medical treatment, access to information may be restricted in accordance with applicable privacy legislation and professional obligations.
                </p>

                {/* Email Communications Section */}
                <h3 style={styles.sectionTitle}>EMAIL AND ELECTRONIC COMMUNICATION</h3>
                <p style={styles.paragraph}>
                    {clinicName} may, from time to time, communicate with you via email with respect to our Services, and in so doing, may use some or all of the information you provide us with when registering for a User Account. The contents of those emails may include, but are not limited to, confirmations of appointments, laboratory and/or radiology requisitions, and brief notes regarding your medical condition. {clinicName} will take all reasonable steps to ensure that the transmission of any such information to you is done as securely as possible, and will only transmit as much information, including personally identifiable information, as is necessary for the purposes for which any such communications are made. You agree to allow {clinicName} to transmit any such information via email. If you do not agree to such transmission, please contact {clinicName}, and alternative arrangements may be made to allow you to retrieve this information.
                </p>

                {/* Contact Section */}
                <div style={styles.contactBlock}>
                    <strong>Contact Us</strong>
                    <p style={{ margin: '0.5rem 0 0 0' }}>
                        If you have any questions or concerns about these Terms, the Privacy Policy, or the Fee Schedule, you may contact us at <a href={`mailto:${serviceEmail}`} style={styles.link}>{serviceEmail}</a>
                    </p>
                </div>

                {/* Fees Modal */}
                <Dialog
                    open={this.state.showFeesModal}
                    onClose={this.handleFeesModalClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{"Fees"}</DialogTitle>
                    <DialogContent>
                        {/* Fee content can be added here */}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleFeesModalClose} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    render() {
        const { openTermsOfUse } = this.props;

        return (
            <AgreementDialog
                open={openTermsOfUse}
                title="Terms of Use"
                content={this.termsOfUserContent()}
                onClose={(checked) => this.handleClose(checked)}
            />
        );
    }
}

TermsOfUseDialog.propTypes = {
    dispatch: PropTypes.func,
    openTermsOfUse: PropTypes.bool
};

function mapStateToProps(state) {
    let { openTermsOfUse } = state.registration;

    if (openTermsOfUse === undefined) {
        openTermsOfUse = false;
    }

    return {
        openTermsOfUse
    };
}

const temp = connect(mapStateToProps)(TermsOfUseDialog);

export { temp as TermsOfUseDialog };
